import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X, Zap, Sparkles, Shield, TrendingUp, AlertCircle, CheckCircle, ChevronDown, AlertTriangle } from "lucide-react";
import { get, post } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlanFeature {
  text: string;
  category: string | null;
  included: boolean;
}

interface Plan {
  slug: string;
  name: string;
  tagline: string;
  monthly_price: number;
  annual_price: number;
  credits_label: string;
  is_popular: boolean;
  features: PlanFeature[];
}

interface ComparisonRow {
  feature: string;
  silver: boolean | string;
  gold: boolean | string;
  platinum: boolean | string;
}

interface Faq {
  q: string;
  a: string;
}

interface PricingData {
  plans: Plan[];
  comparison: ComparisonRow[];
  faqs: Faq[];
}

interface Subscription {
  plan: string;
  plan_interval: string;
  plan_status: string;
  stripe_subscription_id: string | null;
  trial_ends_at: string | null;
  subscription_ends_at: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLAN_VISUALS: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  silver: { icon: Zap, color: "var(--sky)", bg: "var(--sky-light)" },
  gold: { icon: Sparkles, color: "var(--amber)", bg: "var(--amber-light)" },
  platinum: { icon: Shield, color: "#7C3AED", bg: "#EDE9FE" },
};

const PLAN_ORDER: Record<string, number> = { silver: 1, gold: 2, platinum: 3 };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function UpgradePlanPage() {
  const [annual, setAnnual] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [downgradeTarget, setDowngradeTarget] = useState<{ slug: string; interval: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { refreshUser, user } = useAuth();

  const isTrialExpired =
    user?.planStatus === 'trialing' &&
    !!user?.organization?.trial_ends_at &&
    new Date() > new Date(user.organization.trial_ends_at);

  const isPlanInactive =
    user?.planStatus === 'cancelled' || user?.planStatus === 'unpaid';

  const isAccessBlocked = isTrialExpired || isPlanInactive;

  // If user is on a named plan (silver/gold/platinum), they already have a plan selected.
  // Just show an "Activate" button for that plan — no need to pick from the grid.
  const NAMED_PLANS = ['silver', 'gold', 'platinum'];
  const hasNamedPlan = NAMED_PLANS.includes(user?.plan ?? '');
  // Show the quick-activate panel when access is blocked OR user is proactively upgrading
  // from a named plan trial (they already know what plan they're on).
  const showActivatePanel = hasNamedPlan;

  const { data: pricingData, isLoading: pricingLoading } = useQuery({
    queryKey: ["pricing"],
    queryFn: () => get<PricingData>("pricing"),
    staleTime: 0,
  });

  const { data: subscriptionData, isLoading: subLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: () => get<Subscription>("subscription"),
  });

  const plans = pricingData?.data.plans ?? [];
  const comparison = pricingData?.data.comparison ?? [];
  const faqs = pricingData?.data.faqs ?? [];
  const sub = subscriptionData?.data;

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4500);
  };

  const hasVerified = useRef(false);

  useEffect(() => {
    if (hasVerified.current) return;

    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");

    if (paymentStatus !== "success" || !sessionId) return;

    hasVerified.current = true;
    setSearchParams({}, { replace: true });
    setVerifying(true);

    get<{ status: string; plan: string; plan_status: string }>(`billing/verify-session/${sessionId}`)
      .then(async (res) => {
        if (res.data.status === "paid") {
          queryClient.invalidateQueries({ queryKey: ["subscription"] });
          queryClient.invalidateQueries({ queryKey: ["billing"] });
          queryClient.invalidateQueries({ queryKey: ["credits-balance"] });
          await refreshUser();
          showToast(
            "success",
            `Payment successful! Your plan has been upgraded to ${res.data.plan.charAt(0).toUpperCase() + res.data.plan.slice(1)}.`,
          );
        }
      })
      .catch(() => {
        showToast("error", "Payment received, but we had trouble refreshing your plan. Please refresh the page.");
      })
      .finally(() => {
        setVerifying(false);
      });
  }, [searchParams, setSearchParams, queryClient, refreshUser]);

  const isDowngrade = (planSlug: string) => {
    const currentOrder = PLAN_ORDER[sub?.plan ?? ""] ?? 0;
    const targetOrder  = PLAN_ORDER[planSlug] ?? 0;
    return sub?.plan_status === "active" && !!sub?.stripe_subscription_id && targetOrder < currentOrder;
  };

  const handlePlanAction = (planSlug: string) => {
    const interval = annual ? "yearly" : "monthly";
    if (isDowngrade(planSlug)) {
      setDowngradeTarget({ slug: planSlug, interval });
      return;
    }
    executePlanAction(planSlug, interval);
  };

  const executePlanAction = async (planSlug: string, interval: string) => {
    setLoading(planSlug);
    setDowngradeTarget(null);

    try {
      // All plan actions (upgrade, downgrade, new subscription) go through Stripe checkout.
      // For plan changes (org has existing subscription), the backend passes the old
      // subscription ID in checkout metadata so it gets cancelled after payment.
      const endpoint = sub?.stripe_subscription_id ? "billing/change-plan" : "billing/checkout";
      const res = await post<{ checkout_url: string }>(
        endpoint,
        { plan: planSlug, interval },
        { headers: { "Idempotency-Key": `checkout-${planSlug}-${Date.now()}` } },
      );
      window.location.href = res.data.checkout_url;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      showToast("error", message);
    } finally {
      setLoading(null);
    }
  };

  const currentInterval = annual ? "yearly" : "monthly";

  const isCurrentPlan = (planSlug: string) =>
    sub?.plan === planSlug &&
    (sub?.plan_interval ?? "monthly") === currentInterval &&
    ["active", "trialing"].includes(sub?.plan_status ?? "");

  const isExpiredPlan = (planSlug: string) =>
    sub?.plan === planSlug &&
    (sub?.plan_interval ?? "monthly") === currentInterval &&
    ["past_due", "cancelled"].includes(sub?.plan_status ?? "");

  const getButtonLabel = (planSlug: string): string => {
    const isSamePlanAndInterval = sub?.plan === planSlug && (sub?.plan_interval ?? "monthly") === currentInterval;
    const isActive = isSamePlanAndInterval && ["active", "trialing"].includes(sub?.plan_status ?? "");
    const isExpired = isSamePlanAndInterval && ["past_due", "cancelled"].includes(sub?.plan_status ?? "");
    if (isActive) return "Current Plan";
    if (isExpired) return "Reactivate";
    // Same plan slug, different interval
    if (sub?.plan === planSlug && (sub?.plan_interval ?? "monthly") !== currentInterval) {
      return currentInterval === "yearly" ? "Switch to Yearly" : "Switch to Monthly";
    }
    const currentOrder = PLAN_ORDER[sub?.plan ?? ""] ?? 0;
    const targetOrder = PLAN_ORDER[planSlug] ?? 0;
    if (!sub?.plan || currentOrder === 0) return "Get Started";
    return targetOrder > currentOrder ? "Upgrade" : "Downgrade";
  };

  const statusLabel = () => {
    const map: Record<string, string> = {
      active: "Active",
      trialing: "Trial",
      past_due: "Past Due",
      cancelled: "Cancelled",
    };
    return map[sub?.plan_status ?? ""] ?? sub?.plan_status ?? "";
  };

  const statusColor = () => {
    const map: Record<string, string> = {
      active: "#2e7d32",
      trialing: "#F57F17",
      past_due: "#c62828",
      cancelled: "#616161",
    };
    return map[sub?.plan_status ?? ""] ?? "#616161";
  };

  return (
    <div className="up-page upgrade-plan-container">
      {/* Toast */}
      {toast && (
        <div className={`up-toast up-toast--${toast.type}`}>
          {toast.type === "success" ? (
            <CheckCircle size={18} style={{ flexShrink: 0 }} />
          ) : (
            <AlertCircle size={18} style={{ flexShrink: 0 }} />
          )}
          {toast.message}
        </div>
      )}

      {verifying && <div className="up-toast up-toast--info">Confirming your payment…</div>}

      {/* Downgrade confirm modal */}
      {downgradeTarget && (() => {
        const targetPlan = downgradeTarget.slug;
        const currentPlan = sub?.plan ?? '';
        return (
          <div className="up-modal-overlay" onClick={() => setDowngradeTarget(null)}>
            <div className="up-modal" onClick={e => e.stopPropagation()}>
              <div className="up-modal-icon">
                <TrendingUp size={24} style={{ transform: 'rotate(180deg)' }} />
              </div>
              <h3 className="up-modal-title">Downgrade to {targetPlan.charAt(0).toUpperCase() + targetPlan.slice(1)}?</h3>
              <p className="up-modal-body">
                You're moving from <strong>{currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}</strong> to <strong>{targetPlan.charAt(0).toUpperCase() + targetPlan.slice(1)}</strong>.
                You'll be redirected to Stripe to complete payment. Your remaining credits will be carried over and added to the new plan's allocation. No refund is issued for unused time on your current plan.
              </p>
              <div className="up-modal-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => setDowngradeTarget(null)}>
                  Keep {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
                </button>
                <button
                  className="btn btn-sm up-modal-confirm-btn"
                  disabled={!!loading}
                  onClick={() => executePlanAction(targetPlan, downgradeTarget.interval)}
                >
                  {loading ? 'Processing…' : 'Yes, Downgrade'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Plan Expired / Access Blocked Hero */}
      {isAccessBlocked && (
        <div className="up-expired-hero">
          <div className="up-expired-hero__icon">
            <AlertTriangle size={36} color="#dc2626" />
          </div>
          <h2 className="up-expired-hero__title">
            {isTrialExpired ? 'Your trial has expired' : 'Your subscription is inactive'}
          </h2>
          <p className="up-expired-hero__msg">
            {isTrialExpired
              ? <>Your free trial ended on <strong>{new Date(user!.organization!.trial_ends_at!).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>. All features are currently locked. Choose a plan below to restore full access instantly.</>
              : <>Your subscription is no longer active. Choose a plan below to restore access to your account and all its data.</>}
          </p>
          <div className="up-expired-hero__steps">
            <div className="up-expired-hero__step">
              <span className="up-expired-hero__step-num">1</span>
              <span>Choose a plan below</span>
            </div>
            <div className="up-expired-hero__step-arrow">→</div>
            <div className="up-expired-hero__step">
              <span className="up-expired-hero__step-num">2</span>
              <span>Complete payment via Stripe</span>
            </div>
            <div className="up-expired-hero__step-arrow">→</div>
            <div className="up-expired-hero__step">
              <span className="up-expired-hero__step-num">3</span>
              <span>Full access restored immediately</span>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="up-header">
        <div className="up-header__title-row">
          <TrendingUp size={22} color="var(--sky)" />
          <h1 className="up-header__title">Upgrade Plan</h1>
        </div>
        <p className="up-header__subtitle">
          Manage your subscription. Plan changes take effect immediately with proration.
        </p>
      </div>

      {/* Current Plan Banner */}
      {!subLoading && sub && !isAccessBlocked && !['cancelled', 'unpaid'].includes(sub.plan_status) && (
        <div className="card up-current-plan" style={{ borderLeft: `4px solid ${statusColor()}` }}>
          <div>
            <p className="up-current-plan__label">Current Plan</p>
            <p className="up-current-plan__name">
              {sub.plan ? sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1) : "Trial"} Plan
              <span className="up-current-plan__interval">
                · {(sub.plan_interval ?? "monthly") === "yearly" ? "Yearly" : "Monthly"}
              </span>
              <span style={{ marginLeft: 6, fontSize: 13, fontWeight: 500, color: statusColor() }}>
                · {statusLabel()}
              </span>
            </p>
            {sub.trial_ends_at && sub.plan_status === "trialing" && (
              <p className="up-current-plan__trial">
                Trial ends{" "}
                {new Date(sub.trial_ends_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            )}
            {sub.subscription_ends_at && sub.plan_status === "past_due" && (
              <p className="up-current-plan__expired">
                Subscription expired{" "}
                {new Date(sub.subscription_ends_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Quick Activate Panel — shown when user already has a named plan */}
      {showActivatePanel && (() => {
        const currentPlan = plans.find(p => p.slug === user?.plan);
        if (!currentPlan) return null;
        const visuals = PLAN_VISUALS[currentPlan.slug] ?? { icon: Zap, color: 'var(--sky)', bg: 'var(--sky-light)' };
        const PlanIcon = visuals.icon;
        const price = annual ? currentPlan.annual_price : currentPlan.monthly_price;
        const isActive = sub?.plan_status === 'active' && !isAccessBlocked;
        const isActivating = loading === currentPlan.slug;

        return (
          <div className="up-activate-panel" style={{ borderColor: visuals.color }}>
            <div className="up-activate-left">
              <div className="up-activate-icon" style={{ background: visuals.bg, color: visuals.color }}>
                <PlanIcon size={24} />
              </div>
              <div>
                <p className="up-activate-plan-name" style={{ color: visuals.color }}>{currentPlan.name} Plan</p>
                <p className="up-activate-tagline">{currentPlan.tagline}</p>
                <p className="up-activate-credits">{currentPlan.credits_label}</p>
              </div>
            </div>
            <div className="up-activate-right">
              <div className="up-activate-price">
                <span className="up-activate-price__amount">${price}</span>
                <span className="up-activate-price__unit">/mo</span>
                {annual && <span className="up-activate-price__note">billed ${currentPlan.annual_price * 12}/yr</span>}
              </div>
              <div className="up-activate-toggle">
                <button
                  onClick={() => setAnnual(false)}
                  className={`up-activate-toggle__btn${!annual ? ' up-activate-toggle__btn--active' : ''}`}
                  style={!annual ? { background: visuals.color } : undefined}
                >Monthly</button>
                <button
                  onClick={() => setAnnual(true)}
                  className={`up-activate-toggle__btn${annual ? ' up-activate-toggle__btn--active' : ''}`}
                  style={annual ? { background: visuals.color } : undefined}
                >Yearly <span className="up-activate-save">-20%</span></button>
              </div>
              {isActive ? (
                <div className="up-activate-active-badge">
                  <Check size={14} /> Plan Active
                </div>
              ) : (
                <button
                  className="up-activate-btn"
                  style={{ background: visuals.color }}
                  disabled={isActivating}
                  onClick={() => handlePlanAction(currentPlan.slug)}
                >
                  {isActivating ? 'Processing…' : `Activate ${currentPlan.name} Plan`}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Other plans heading — only when user has a named plan and wants to switch */}
      {showActivatePanel && (
        <p className="up-or-choose">— or choose a different plan —</p>
      )}

      {/* Monthly / Yearly Toggle */}
      <div className="up-toggle">
        <div className="up-toggle__inner">
          <button
            onClick={() => setAnnual(false)}
            className={`up-toggle__btn${!annual ? " up-toggle__btn--active" : ""}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setAnnual(true)}
            className={`up-toggle__btn up-toggle__btn--yearly${annual ? " up-toggle__btn--active" : ""}`}
          >
            Yearly
            <span className="up-toggle__save-badge">Save 20%</span>
          </button>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="up-plans-grid">
        {pricingLoading && [1, 2, 3].map((i) => <div key={i} className="card up-plan-skeleton" />)}

        {plans.map((plan) => {
          const visuals = PLAN_VISUALS[plan.slug] ?? { icon: Zap, color: "var(--sky)", bg: "var(--sky-light)" };
          const PlanIcon = visuals.icon;
          const current = isCurrentPlan(plan.slug) && !isAccessBlocked;
          const expired = isExpiredPlan(plan.slug);
          const btnLabel = getButtonLabel(plan.slug);
          const isLoading = loading === plan.slug;
          const price = annual ? plan.annual_price : plan.monthly_price;

          return (
            <div
              key={plan.slug}
              className="card up-plan-card"
              style={{
                border: current
                  ? `2px solid ${visuals.color}`
                  : expired
                    ? "2px solid #EF9A9A"
                    : "2px solid transparent",
              }}
            >
              {current && !isAccessBlocked && (
                <div className="up-plan-badge" style={{ background: visuals.color }}>
                  CURRENT PLAN
                </div>
              )}
              {expired && <div className="up-plan-badge up-plan-badge--expired">EXPIRED</div>}
              {plan.is_popular && !current && !expired && (
                <div className="up-plan-badge up-plan-badge--popular">MOST POPULAR</div>
              )}

              <div className="up-plan-header">
                <div className="up-plan-icon" style={{ background: visuals.bg, color: visuals.color }}>
                  <PlanIcon size={20} />
                </div>
                <div>
                  <h3 className="up-plan-name">{plan.name}</h3>
                  <p className="up-plan-tagline">{plan.tagline}</p>
                </div>
              </div>

              <div className="up-plan-price">
                <span className="up-plan-price__amount">${price}</span>
                <span className="up-plan-price__unit">/mo</span>
              </div>
              {annual && <p className="up-plan-price__annual">Billed ${plan.annual_price * 12}/year</p>}
              <p className="up-plan-credits">{plan.credits_label}</p>

              <button
                onClick={() => !current && handlePlanAction(plan.slug)}
                disabled={current || isLoading}
                className="up-plan-btn"
                style={
                  {
                    cursor: current ? "default" : "pointer",
                    opacity: isLoading ? 0.7 : 1,
                    background: current ? "var(--gray-100)" : btnLabel === "Downgrade" ? "transparent" : "var(--green)",
                    color: current ? "var(--gray-400)" : btnLabel === "Downgrade" ? "var(--gray-500)" : "#fff",
                    outline: btnLabel === "Downgrade" ? "1px solid var(--gray-300)" : "none",
                  } as React.CSSProperties
                }
              >
                {isLoading ? "Processing…" : btnLabel}
              </button>

              <div className="up-features">
                {(() => {
                  const groups: { category: string | null; items: PlanFeature[] }[] = [];
                  const seen = new Map<string | null, PlanFeature[]>();
                  for (const f of plan.features) {
                    const key = f.category ?? null;
                    if (!seen.has(key)) {
                      const items: PlanFeature[] = [];
                      seen.set(key, items);
                      groups.push({ category: key, items });
                    }
                    seen.get(key)!.push(f);
                  }
                  return groups.map((group, gi) => (
                    <div key={group.category ?? `__ungrouped_${gi}`}>
                      {group.category && (
                        <div className={`up-feature-category${gi === 0 ? ' up-feature-category--first' : ''}`}>
                          {group.category}
                        </div>
                      )}
                      {group.items.map((f) => (
                        <div key={f.text} className="up-feature" style={{ opacity: f.included ? 1 : 0.4 }}>
                          {f.included ? (
                            <Check size={15} color="var(--green)" style={{ flexShrink: 0, marginTop: 2 }} />
                          ) : (
                            <X size={15} color="var(--gray-300)" style={{ flexShrink: 0, marginTop: 2 }} />
                          )}
                          <span className="up-feature__text">{f.text}</span>
                        </div>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Feature Comparison Table */}
      {comparison.length > 0 && (
        <div className="up-comparison">
          <h2 className="up-section-heading">Full feature comparison</h2>
          <div className="up-table-wrap">
            <table className="up-table">
              <thead>
                <tr>
                  <th className="up-table__feature-th">Feature</th>
                  {plans.map((plan) => {
                    const visuals = PLAN_VISUALS[plan.slug] ?? { color: "var(--sky)" };
                    return (
                      <th
                        key={plan.slug}
                        className={`up-table__plan-th${plan.is_popular ? " up-table__plan-th--popular" : ""}`}
                        style={{ color: plan.is_popular ? visuals.color : "var(--navy)" }}
                      >
                        {plan.name}
                        {isCurrentPlan(plan.slug) && (
                          <span className="up-table__your-plan" style={{ color: visuals.color }}>
                            YOUR PLAN
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, i) => (
                  <tr key={row.feature} style={{ background: i % 2 === 0 ? "#fff" : "var(--gray-50)" }}>
                    <td className="up-table__feature-td">{row.feature}</td>
                    {plans.map((plan) => {
                      const val = row[plan.slug as keyof ComparisonRow];
                      const visuals = PLAN_VISUALS[plan.slug] ?? { color: "var(--sky)" };
                      return (
                        <td
                          key={plan.slug}
                          className={`up-table__value-td${plan.is_popular ? " up-table__value-td--popular" : ""}`}
                        >
                          {typeof val === "boolean" ? (
                            val ? (
                              <Check size={16} color="var(--green)" style={{ margin: "0 auto", display: "block" }} />
                            ) : (
                              <X size={16} color="var(--gray-200)" style={{ margin: "0 auto", display: "block" }} />
                            )
                          ) : (
                            <span
                              style={{
                                color: plan.is_popular ? visuals.color : "var(--gray-700)",
                                fontWeight: plan.is_popular ? 600 : 400,
                              }}
                            >
                              {val as string}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="up-faq">
          <h2 className="up-section-heading">Frequently asked questions</h2>
          <div className="up-faq__list">
            {faqs.map((faq, i) => (
              <div key={i} className="up-faq__item">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="up-faq__btn">
                  <span>{faq.q}</span>
                  <ChevronDown
                    size={18}
                    color="var(--gray-400)"
                    className="up-faq__chevron"
                    style={{ transform: openFaq === i ? "rotate(180deg)" : "rotate(0deg)" }}
                  />
                </button>
                {openFaq === i && <div className="up-faq__answer">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="up-footer-note">
        Payments processed securely via Stripe · Cancel anytime · Changes take effect immediately
      </p>
    </div>
  );
}
